import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useHandleErrors from '../../hooks/useHandleErrors';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { useFetch } from '../../hooks/useFetch';
import LoadingContent, { ErrorContent } from '../../components/LoadingContent';
import {
    isAfter,
    isBefore,
    isSameHour,
    isPast,
    parseISO,
    subHours,
} from 'date-fns';
import { getServerNow, useServerTime } from '../../hooks/useServerTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const SectionCard = ({ children, onClick, sx }) => (
    <Box
        onClick={onClick}
        sx={{
            width: '100%',
            bgcolor: 'white',
            borderRadius: '16px',
            border: '1px solid #e8e8e8',
            p: { xs: '16px', sm: '20px 24px' },
            cursor: onClick ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            ...(onClick && {
                '&:hover': {
                    borderColor: '#006a61',
                    boxShadow: '0 2px 12px rgba(0,106,97,0.08)',
                },
            }),
            ...sx,
        }}
    >
        {children}
    </Box>
);

// Vaga desfeita ganha o rotulo antes de qualquer outra coisa: um encontro
// cancelado cujo horario ja passou continua sendo "Cancelado", nao "Finalizado"
const getStatusChip = (datetime, status, finished) => {
    if (status === 'CANCELED') {
        return <Chip label="Cancelado" size="small" sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '0.7rem' }} />;
    }
    if (status === 'REFUNDED') {
        return <Chip label="Estornado" size="small" sx={{ bgcolor: '#fce4ec', color: '#c62828', fontWeight: 500, fontSize: '0.7rem' }} />;
    }
    if (finished) {
        return <Chip label="Finalizado" size="small" sx={{ bgcolor: '#f5f5f5', color: '#888', fontWeight: 500, fontSize: '0.7rem' }} />;
    }
    if (status === 'PENDING') {
        return <Chip label="Pagamento pendente" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 500, fontSize: '0.7rem' }} />;
    }
    if (isSameHour(getServerNow(), parseISO(datetime))) {
        return <Chip label="Acontecendo agora" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }} />;
    }
    if (isPast(parseISO(datetime))) {
        return <Chip label="Finalizado" size="small" sx={{ bgcolor: '#f5f5f5', color: '#888', fontWeight: 500, fontSize: '0.7rem' }} />;
    }
    return <Chip label="Confirmado" size="small" sx={{ bgcolor: '#e6f3f1', color: '#006a61', fontWeight: 500, fontSize: '0.7rem' }} />;
};

const MeetingItem = ({ id, meeting, datetime, status, finished, rescheduled }) => {
    const navigation = useNavigate();
    const meetingDate = new Date(datetime);
    const formattedDate = meetingDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    const formattedTime = meetingDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const hostUser = meeting?.Profile?.User;

    return (
        <SectionCard onClick={() => navigation(`/encontros/${id}/detalhes`)}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                    src={hostUser?.profileImage}
                    alt={hostUser?.name}
                    sx={{ width: 44, height: 44, border: '2px solid #e0e0e0', flexShrink: 0 }}
                >
                    {hostUser?.name?.[0]}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {meeting?.title || 'Encontro removido'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                        com @{hostUser?.username}
                    </Typography>
                </Box>

                <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                    {getStatusChip(datetime, status, finished)}
                    {/* Anfitriao mudou o horario e o conviva ainda nao decidiu:
                        o detalhe explica e oferece o cancelamento */}
                    {rescheduled && ['CONCLUDED', 'PENDING'].includes(status) && (
                        <Chip
                            label="Horário alterado"
                            size="small"
                            sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.65rem', height: 20 }}
                        />
                    )}
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <CalendarTodayIcon sx={{ fontSize: 12, color: '#999' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {formattedDate}
                        </Typography>
                        <AccessTimeIcon sx={{ fontSize: 12, color: '#999', ml: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {formattedTime}
                        </Typography>
                    </Stack>
                </Stack>

                <ChevronRightIcon sx={{ color: '#ccc', fontSize: 20, flexShrink: 0 }} />
            </Stack>
        </SectionCard>
    );
};

const ScheduledList = () => {
    useServerTime(); // Garante sincronização com servidor
    const [tab, setTab] = useState(0);
    const navigation = useNavigate();
    // include_canceled e so desta tela: o calendario usa a mesma rota e nao
    // deve exibir vaga cancelada como compromisso
    const { data, error, isLoading } = useFetch('/booking', { include_canceled: true });
    useHandleErrors(error);

    if (isLoading) return <LoadingContent />;
    if (error) return <ErrorContent label="Erro na requisição" />;

    // Vaga desfeita nunca e "proximo encontro", mesmo com a data no futuro:
    // nao ha nada a comparecer
    const desfeito = item => ['CANCELED', 'REFUNDED'].includes(item.status);

    // Encontro finalizado vai direto para o historico, mesmo que o horario
    // marcado ainda esteja dentro da janela dos "proximos"
    const future = data?.filter(
        item => !desfeito(item) && !item.finished && isBefore(subHours(getServerNow(), 1), parseISO(item.datetime)),
    ) || [];
    const past = data?.filter(
        item => desfeito(item) || item.finished || isAfter(subHours(getServerNow(), 2), parseISO(item.datetime)),
    ) || [];

    const currentList = tab === 0 ? future : past;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 640,
            mx: 'auto',
            p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
            gap: 2,
        }}>
            <Typography variant="h6" fontWeight={600} sx={{ alignSelf: 'flex-start' }}>
                Encontros comprados/agendados
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'flex-start', mt: -1 }}>
                Encontros em que você participa como conviva.
            </Typography>

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                    alignSelf: 'flex-start',
                    minHeight: 36,
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        minHeight: 36,
                        fontSize: '0.85rem',
                    },
                    '& .Mui-selected': { color: '#006a61' },
                    '& .MuiTabs-indicator': { backgroundColor: '#006a61' },
                }}
            >
                <Tab label={`Próximos (${future.length})`} />
                <Tab label={`Histórico (${past.length})`} />
            </Tabs>

            <Stack spacing={1.5} sx={{ width: '100%' }}>
                {currentList.length === 0 && (
                    <Alert severity="info" sx={{ borderRadius: '12px' }}>
                        {tab === 0
                            ? 'Nenhum encontro agendado. Explore encontros disponíveis!'
                            : 'Nenhum encontro no histórico.'
                        }
                    </Alert>
                )}

                {currentList.map(({ id, meeting, datetime, status, finished, rescheduled_at }) => (
                    <MeetingItem
                        key={id}
                        id={id}
                        meeting={meeting}
                        datetime={datetime}
                        status={status}
                        rescheduled={Boolean(rescheduled_at)}
                        finished={finished}
                    />
                ))}
            </Stack>

            {tab === 0 && future.length === 0 && (
                <Button
                    variant="contained"
                    onClick={() => navigation('/lista')}
                    sx={{
                        bgcolor: '#006a61',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        mt: 1,
                        '&:hover': { bgcolor: '#005a52' },
                    }}
                >
                    Explorar encontros
                </Button>
            )}
        </Box>
    );
};

export default ScheduledList;
